import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const resetSuccess = searchParams.get('reset') === 'success';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, totpCode || undefined);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      if (detail === '2FA-Code erforderlich') {
        setShow2FA(true);
        setError('');
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#181c34',
        background: 'radial-gradient(ellipse at 50% 0%, #1e2340 0%, #181c34 70%)',
      }}
    >
      {/* Subtle grid pattern overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            'linear-gradient(#2e356008 1px, transparent 1px), linear-gradient(90deg, #2e356008 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      <Card
        sx={{
          width: { xs: '100%', sm: 440 },
          maxWidth: '100%',
          mx: { xs: 2, sm: 'auto' },
          bgcolor: '#212744',
          border: '1px solid #2e3560',
          borderRadius: 3,
          position: 'relative',
          overflow: 'visible',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        {/* Top accent line */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #3dd47e, transparent)',
            borderRadius: '2px',
          }}
        />

        <CardContent sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/stark_logo.png"
              alt="STARK IT-Security"
              sx={{
                maxWidth: '80%',
                height: 64,
                objectFit: 'contain',
                mx: 'auto',
                mb: 2,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#667788',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                mt: 0.5,
              }}
            >
              IT-Security Management
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                bgcolor: '#2a1520',
                border: '1px solid #5c2030',
                '& .MuiAlert-icon': { color: '#ef5350' },
              }}
            >
              {error}
            </Alert>
          )}
          {resetSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 2,
                bgcolor: '#123025',
                border: '1px solid #2ba862',
                '& .MuiAlert-icon': { color: '#3dd47e' },
              }}
            >
              Passwort wurde erfolgreich zurückgesetzt. Bitte melde dich an.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="E-Mail"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              size="medium"
            />
            <TextField
              label="Passwort"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {show2FA && (
              <TextField
                label="2FA-Code"
                fullWidth
                margin="normal"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="6-stelliger Code"
                autoFocus
              />
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{
                mt: 3,
                py: 1.5,
                fontSize: '0.95rem',
                bgcolor: '#3dd47e',
                color: '#181c34',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: '#2ba862',
                  boxShadow: '0 0 30px #3dd47e33',
                },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#181c34' }} /> : 'Anmelden'}
            </Button>
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#8899aa' }}>
              <RouterLink to="/passwort-vergessen" style={{ color: '#3dd47e', textDecoration: 'none' }}>
                Passwort vergessen?
              </RouterLink>
            </Typography>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
